import {UIComponent} from "@/engine/UI/UIComponent.js";
import {UICExportHelper} from "@/engine/UIcomponents/UICExportHelper.js";
import {sm} from "@/context/global.js";

export class UICExportPdf extends UIComponent {
    constructor(options, view = "pdfExportWindowContent") {
        super(options, view);
        this.id = 'pdfExport'
        this.title = 'Create pdf'
        this.mainActionName = 'CREATE PDF'
        this.cancelActionName = 'CANCEL'
        this.className = 'pdf-control'
        if (!this.pdf) {
            console.error("pdf isn't defined")
        }
        if (!this.map) {
            console.error("map isn't defined")
        }
        this.progressCurrentImageNo = 0
        this.progressTotalImages = 0
        this.abortReason = ''
    }

    open(container, parent, data) {
        data = {}
        if (this.area && this.area instanceof L.Polyline) {
            data.isGeometry = true
        }

        data.hasRasterArea = this.rasterArea

        super.open(container, parent, data)

        let el = this.el.bind(this)

        let pdf = this.pdf
        pdf.setArea(this.area)

        this.progressNode = document.createElement("div")
        Object.assign(this.progressNode, {className: 'pdf-progress'});
        this.pdf.progressDiv.append(this.progressNode)
        this.progressLabel = document.createElement("div")
        this.progressNode.append(this.progressLabel)

        let pagePresetElement = el('preset')
        let defaultPageFormat = pdf.getPageFormat()
        let id = 1;
        for (let pageFormat of pdf.getPageFormats()) {
            pagePresetElement.append(new Option(pageFormat.name));
            if (defaultPageFormat &&
                defaultPageFormat.name === pageFormat.name) {
                pagePresetElement.selectedIndex = id
                el('width').value = pageFormat.width;
                el('height').value = pageFormat.height;
            }
            id++
        }

        if (! defaultPageFormat) {
            pagePresetElement.selectedIndex = 0
            el('width').value = pdf.pageSize.width;
            el('height').value = pdf.pageSize.height;
        }
        el('margin').value = pdf.getPageMargin()

        // create event handlers
        el('pages').addEventListener("change", this._updateSettings.bind(this));
        el('width').addEventListener("change", function () {
            el('preset').selectedIndex = 0
            this._updateSettings()
        }.bind(this));
        el('height').addEventListener("change", function () {
            el('preset').selectedIndex = 0
            this._updateSettings()
        }.bind(this));

        el('preset').addEventListener("change", function(event) {
            if (el('preset').selectedIndex > 0) { // 0 is "free"
                el('width').value = pdf.getPageFormats()[el('preset').selectedIndex-1].width;
                el('height').value = pdf.getPageFormats()[el('preset').selectedIndex-1].height;
                this._updateSettings()
            }
        }.bind(this));
        el('orientation').addEventListener("change", this._updateSettings.bind(this));
        el('margin').addEventListener("change", this._updateSettings.bind(this))

        this.map.on("imagePdf:progress", this._onProgress, this)
        this.map.on("imagePdf:aborted", this._onAborted, this)

        this._updateSettings()
    }

    close() {
        this.pdf.setArea()
        this.map.off("imagePdf:progress", this._onProgress, this)
        this.map.off("imagePdf:aborted", this._onAborted, this)
        this.progressNode.remove()
        super.close()
    }

    onMainAction() {
        // parses options and tunes pdf module
        let savedExcludeNodesWithCSS = this.pdf.options.excludeNodesWithCSS
        let excludeNodesWithCSS = UICExportHelper._createLayersFilterFromCheckboxes(this.el.bind(this))
        this.pdf.options.excludeNodesWithCSS = UICExportHelper._addToTargetArray(this.pdf.options.excludeNodesWithCSS, excludeNodesWithCSS)

        this.map.once("imagePdf:finish", function (data) {
            if (data.blob) {
                sm.noticeMessage("Pdf has been created, see browser download files.")
            } else {
                if (this.abortReason === '') {
                    sm.noticeMessage("Pdf creating has been aborted due the error.")
                } else {
                    sm.noticeMessage("Pdf export operation has been aborted due the error: " + this.abortReason)
                }
            }
            this.pdf.options.excludeNodesWithCSS = savedExcludeNodesWithCSS
        }.bind(this))
        this.pdf.setImageFormat('jpeg')
        this.pdf.createPdf()
        return false
    }

    onCancelAction() {
        this.close()
        super.onCancelAction();
        return true
    }

    _updateSettings() {
        let el = this.el.bind(this)
        // update common pdf options
        this.pdf.setPageCount(el('pages').value)
        let o = el('orientation').value
        for (o of this.pdf.getPageOrientations()) {
            if (o.name === el('orientation').value) {
                this.pdf.setPageOrientation(o.value)
                break
            }
        }
        this.pdf.setPageMargin(el('margin').value)
        if (el('preset').selectedIndex > 0) { // 0 is "free"
            this.pdf.setPageFormat(el('preset').value)
        } else {
            this.pdf.setPageSize(el('width').value, el('height').value)
        }
        let pd = this.pdf.calcPdfPages()

        if (pd) {
            el("image-spec").hidden = false
            el("image-dpi").innerHTML = `${pd.dpi} DPI`;
            el("image-resolution").innerHTML = `${Math.floor(pd.dimensions.wpx)} x ${Math.floor(pd.dimensions.hpx)}`

            // indicate print quality with color
            let dpi1 = 0, hue1 = 0;     // horrible print quality  (red)
            let dpi2 = 300, hue2 = 140; // excellent print quality (green)
            let hue = Math.min(Math.floor((hue2 - hue1) * (pd.dpi - dpi1) / (dpi2 - dpi1)), hue2); // restrict to hue2
            el("image-dpi").style.color = `hsl(${hue}, 100%, 50%)`;

            this.pdf.showPdfPages(pd)
        } else {
            el("image-spec").hidden = true
        }

        return pd
    }

    _onProgress(p) {
        if (p.operation === "page") {
            this.progressLabel.innerText = `Creating page ${p.itemNo+1} of ${p.totalItems} ...`;
            return
        }
        let percent = 0
        if (p.operation === "image") {
            this.progressCurrentImageNo = p.itemNo+1
            this.progressTotalImages = p.totalItems
        }
        if (p.operation === "tile") {
            percent = Math.ceil((p.itemNo / p.totalItems) * 100)
        }
        this.progressLabel.innerText = `Creating image ${this.progressCurrentImageNo} of ${this.progressTotalImages}: ${percent}%`;

    }

    _onAborted(p) {
        if (p.reason === "timeout") {
            this.abortReason = "timeout with loading map images, check your network connection and try again"
        }
    }
}
